import React, {useEffect, useState, useContext, useRef, useMemo, useReducer} from 'react';
import {FormattedMessage, IntlContext} from 'react-intl';
import { Formik, Form } from "formik";
import * as Yup from 'yup';
import {config} from '@react-spring/web'
import useScrollTo from 'react-spring-scroll-to-hook'
import { API } from 'aws-amplify';
import WETTabs, {clickTab} from '../../components/WETTabs';
import SubmitTabBody from '../../components/SubmitTabBody';
import {constants} from '../../constants'
import Spinner from '../../components/Spinner';
import { currentLang, currentLongLang, getHealthCheck } from '../../components/components/utils';
import AppContext from '../../components/AppContext';
import EFilingMainInfoTabBody from './components/EFilingMainInfoTabBody';
import DetailsTabBody from './components/DetailsTabBody';
import AttachmentsTabBody from './components/AttachmentsTabBody';
import AddClientFormik from './components/AddClientFormik';
import ErrorValidationSpan from './components/ErrorValidationSpan';
import PopupLink from '../../templates/PopupLink';
function SecureEFiling({navigation}) {
	const [, forceUpdate] = useReducer(x => x + 1, 0);
	const appContext = useContext(AppContext);
	const { messages } = useContext(IntlContext);
	const FieldArrayHelpersRef = useRef();

	//note: this labels variable has to contain labels for each field name that has validation against.  The key here has to match the "name" property of the field.
	const labels = useMemo(() => {
		const formatValues = {
			p: (chunks) => <p>{chunks}</p>,
			a: (chunks) => (
				<a
					href={`https://www.cbsa-asfc.gc.ca/contact/bis-sif-${document.documentElement.lang === "fr" ? "fra" : "eng"}.html`}
					className="App-link"
					target="_blank"
					rel="noreferrer"
				>
					{chunks}
				</a>
			),
		};

		return {
			"http.networkError": (
				<FormattedMessage
					id="http.networkError"
					values={formatValues}
				/>
			),
			"http.networkErrorFileServer": (
				<FormattedMessage
					id="http.networkErrorFileServer"
					values={formatValues}
				/>
			),
			"error.tooManyFiles": (
				<FormattedMessage
					id="error.tooManyFiles"
					values={{ num: constants.maxUploadLimitPerSubmission }}
				/>
			),
			"http.networkError.args": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `01-0001-${errorNum}` : "01-0001-Unknown",

					}}
				/>
			),
			"http.networkError.08-001": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `08-001-${errorNum}` : "08-001-Unknown",

					}}
				/>
			), // account lambda error
			"http.networkError.08-005": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `08-005-${errorNum}` : "08-005-Unknown",

					}}
				/>
			), // contact lambda error
			"http.networkError.08-009": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `08-009-${errorNum}` : "08-009-Unknown",

					}}
				/>
			), // getActiveFirmAccounts lambda error
			"http.networkError.08-010": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `08-010-${errorNum}` : "08-010-Unknown",

					}}
				/>
			), // getActiveProceedings lambda error
			"http.networkError.08-014": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `08-014-${errorNum}` : "08-014-Unknown",

					}}
				/>
			), // getRelatedSubmissions lambda error
		};
	}, []); // Empty dependency array since formatValues is static and doesn't depend on intl

	const [showSpinner, setShowSpinner] = useState(false);
	const [activeProceedingsOptions, setActiveProceedingsOptions] = useState([{value:"", label: messages["common.select..."]}]);
	const [activeFirmAccountsOptions, setActiveFirmAccountsOptions] = useState([{value:"", label: messages["common.select..."]}]);
	const [relatedSubmissionsOptions, setRelatedSubmissionsOptions] = useState([{value:"", label: messages["common.select..."]}]);	//default with a something in the array, so the spread operator doesn't break on us

	const [tempSubmissionId] = useState(Date.now()+"");

	const activeProceedingsRef = useRef(activeProceedingsOptions);
	const activeFirmAccountsRef = useRef(activeFirmAccountsOptions);
	const relatedSubmissionsRef = useRef(relatedSubmissionsOptions);

	const tempSubmissionIdRef = useRef(tempSubmissionId);

	const [displayPopup, setDisplayPopup] = useState(false);
	const [accountData, setAccountData] = React.useState({...appContext.accountData});
	const [accountBusinessNumber, setAccountBusinessNumber] = useState(!!accountData.ace_businessnumber ? accountData.ace_businessnumber : "");

	const accountBusinessNumberRef = useRef(accountBusinessNumber);

	const [firstError, setFirstError] = React.useState('');
	const [errorFocus, setErrorFocus] = React.useState(false);

	const setAndSaveIntervalToUpdateCSS = () => {
		let startTime = Date.now();
		intervalIds["show"] = setInterval(() => {
				updateCSS("show", startTime);
			}
			, 1
		);
	}

	useEffect(() => {
		activeProceedingsRef.current = activeProceedingsOptions;
	}, [activeProceedingsOptions]);
	useEffect(() => {
		activeFirmAccountsRef.current = activeFirmAccountsOptions;
	}, [activeFirmAccountsOptions]);
	useEffect(() => {
		relatedSubmissionsRef.current = relatedSubmissionsOptions;
	}, [relatedSubmissionsOptions]);
	useEffect(() => {
		accountBusinessNumberRef.current = accountBusinessNumber;
	}, [accountBusinessNumber]);
	useEffect(() => {
		tempSubmissionIdRef.current = tempSubmissionId;
	})
	useEffect(() => {
		appContext.setDynamicPageTitle(<FormattedMessage id="sima.btn.efiling" />);

		let promisesToRun = [getHealthCheck(appContext, labels["http.networkErrorFileServer"])];

		if (!(accountData?.ace_businessnumber ?? null)) {
			//get account data, if it exists, navigate to Account_Edit if no business number
			promisesToRun.push(getAccountData());
		}
		Promise.all(promisesToRun).catch((err) => {
			if (window.env.environment !== "prod") {
				console.log({err})
			}
		}).finally(() => {
			setShowSpinner(false);
		});

		if (window.wb) {
			window.wb.add(".wb-lbx");
			document.getElementById("tabsDiv").style.opacity = 0;
			window.wb.add(".wb-tabs");
			setTimeout(setAndSaveIntervalToUpdateCSS, 400)
		}
	}, []);

	useEffect(() => {
		const getActiveProceedings = async () => {
			let apiURL = "getActiveProceedings";

			return API.get("backend-api", apiURL, {}).then(function(response){
				if (response.length === 0) {
					setActiveProceedingsOptions([{value:"", label: messages["common.select..."]}, {value:"", label: messages["noProceedingIdentifiers"]}]);
				} else {
					let sortedResponse = response.sort((a, b) => a.label.localeCompare(b.label));
					setActiveProceedingsOptions([{value:"", label: messages["common.select..."]}, ...sortedResponse]);
				}
			}).catch((err) => {
				if (window.env.environment !== "prod") {
					console.log({err})
				}

				//TODO - put up banner message if there is an error caught here, because that means that the list of active proceedings couldn't be retrieved
				if (window.env.environment === "local") {
					//fill with dummy data
					let dummyResponse = [{value:"1", label: "proceeding 1"}, {value:"2", label: "proceeding 2"}];
					setActiveProceedingsOptions([{value:"", label: messages["common.select..."]}, ...dummyResponse]);
				} else {
					let errorNum = err?.response?.data?.Error?.ErrorNumber; // ErrorNumber specific to the lambda

					if (!!errorNum) {
						appContext.setFlashError({duration:-1, messages:[labels["http.networkError.08-010"](errorNum)]});
					} else {
						appContext.setFlashError({duration:-1, messages:[labels["http.networkError"]]});
					}
				}
			}).finally(() => {
				setShowSpinner(false);
				forceUpdate();
			});
		}

		if (activeProceedingsOptions.length === 1) {
			//get active proceedings, if necessary
			getActiveProceedings();
		}
	}, []);

	useEffect(() => {
		const getActiveFirmAccounts = async () => {
			let apiURL = "getActiveFirmAccounts";

			return API.get("backend-api", apiURL, {}).then(function(response){

				if (!!response && response.length > 0) {
					let sortedResponse = response.sort((a, b) => a.label.localeCompare(b.label))
					setActiveFirmAccountsOptions([{value:null, label: messages["common.select..."]}, ...sortedResponse]);
				}

			}).catch((err) => {
				if (window.env.environment === "local") {
					//fill with dummy data
					let dummyActiveFirmAccounts = [{value:null, label: messages["common.select..."]}, ...appContext.codeTables.activeFirmAccounts];
					setActiveFirmAccountsOptions(dummyActiveFirmAccounts);
				}

				if (window.env.environment !== "local") {
					let errorNum = err?.response?.data?.Error?.ErrorNumber; // ErrorNumber specific to the lambda

					if (!!errorNum) {
						appContext.setFlashError({duration:-1, messages:[labels["http.networkError.08-009"](errorNum)]});
					} else {
						appContext.setFlashError({duration:-1, messages:[labels["http.networkError"]]});
					}
				}
			});
		}

		if (activeFirmAccountsOptions.length===1) {
			//get firmAccounts options if necessary
			Promise.all([
				getActiveFirmAccounts()
			]).catch((err) => {
				if (window.env.environment !== "prod") {
					console.log({err})
				}
				appContext.setFlashError({duration:-1, messages:[labels["http.networkError"]]});
			}).finally(() => {
				setShowSpinner(false);
				forceUpdate();
			});
		}
	}, []);

	async function getAccountData() {
		let apiURL = "account";

		return API.get("backend-api", apiURL).then(function(response){
			let responseAccountData = {...response};

			responseAccountData.lastDataRetrievalDate = Date.now();
			appContext.accountData = responseAccountData;

			setAccountData(responseAccountData);
			setAccountBusinessNumber(responseAccountData.ace_businessnumber)

			if (!(responseAccountData?.ace_businessnumber ?? null)) {
				//no business number set, indicate that there's data missing on their account or the system failed to retrieve their account data, redirect them to the edit account page for them to fill in the missing info
				appContext.setFlashError({duration:-3, messages:[messages["error.noBusinessAccount"]]});

				navigation.navigate("Account_Edit");
			}
		}).catch((err) => {
			let errorNum = err?.response?.data?.Error?.ErrorNumber; // ErrorNumber specific to the lambda

			if (!!errorNum) {
				appContext.setFlashError({duration:-1, messages:[labels["http.networkError.08-001"](errorNum)]});
			} else {
				appContext.setFlashError({duration:-1, messages:[labels["http.networkError"]]});
			}

			if (window.env.environment === "local") {
				let dummyAccountData = window.env.accountData;
				appContext.accountData = dummyAccountData;
				setAccountData(dummyAccountData);
				setAccountBusinessNumber(dummyAccountData.ace_businessnumber)
			} else {
				throw err;
			}
		});
	}

	const getRelatedSubmissions = async (values) => {
		let apiURL = "getRelatedSubmissions";
		let apiParamsObj = {
			queryStringParameters: {
				proceedingId: values.proceedingIdentifier,
				typeOfSubmission: values.typeOfSubmission
			}
		}
		await API.get("backend-api", apiURL, apiParamsObj).then(function(response){
			if (!!response && response.length > 0) {
				setRelatedSubmissionsOptions([{value:null, label: messages["common.select..."]}, ...response]);
			} else {
				setRelatedSubmissionsOptions([{value:null, label: messages["sima.related.submission.notFound.option"]}]);
			}
		}).catch(() => {
			//TODO - put up banner message if there is an error caught here, because that means that the list of active proceedings couldn't be retrieved
			if (window.env.environment === "local") {
				//fill with dummy data
				let response = [{value:"1", label: "related submission 1"}, {value:"2", label: "related submission 2"}];
				setRelatedSubmissionsOptions([{value:null, label: messages["common.select..."]}, ...response]);
			}
		});
	}

	let eFilingMainInfo = [
		{
			tabId: "client"
			, tabLabel: <FormattedMessage id="sima.tab.header.1"/>
			, enableTabNumbering: true
			, tabContent:
				<>
				  <EFilingMainInfoTabBody codeTables={appContext.codeTables} navigation={navigation} FieldArrayHelpersRef={FieldArrayHelpersRef} setDisplayPopup={setDisplayPopup} activeProceedings={activeProceedingsRef} activeFirmAccounts={activeFirmAccountsRef} accountDataBusinessNumber={accountBusinessNumberRef}/>
				</>
			, validationSchema: {
				role: Yup.string().required(messages["common.isRequired"])
				, addClient: Yup.string().when('role', {
					is: (role) => !!role && role === constants.roles["Counsel"]
					, then: Yup.string().required(messages["common.isRequired"])
				})
				, clientAccounts: Yup.array().of(
					Yup.object()
				).test({
					name: "noDuplicates"
					, exclusive: false
					, message: messages["error.duplicateClientBNAndRole"]
					, test: function (arr) {
						let noDuplicates = true;
						const accountMap = {};

						arr.forEach((clientAccount)=>{
							const key = `${clientAccount.ace_businessnumber}-${clientAccount.ace_partytype}`
							if (accountMap[key]) {
								noDuplicates = false;
							} else {
								accountMap[key] = true;
							}
						});
						return noDuplicates;
					}
				})
			}
		}
	]
	let details = [
		{
			tabId: "details"
			, tabLabel: <FormattedMessage id="sima.tab.header.2" />
			, enableTabNumbering: true
			, tabContent:
				<>
				  <DetailsTabBody codeTables={appContext.codeTables} navigation={navigation} activeProceedings={activeProceedingsRef} relatedSubmissions={relatedSubmissionsRef} getRelatedSubmissions={getRelatedSubmissions} />
				</>
			, validationSchema: {
				typeOfSubmission: Yup.string().required(messages["common.isRequired"])
				, proceedingIdentifier: Yup.string().when('typeOfSubmission', {
					is: (typeOfSubmission) => !!typeOfSubmission && typeOfSubmission === constants.typeOfSubmissions["Response to Disclosure-Related Deficiency Notice"] // "f8bc4332-9fea-ee11-96ea-0050568199d1"
					, then: Yup.string().required(messages["common.isRequired"])
					, otherwise: Yup.string().notRequired()
				}).test({
					name: 'ifDRDMustSelectASubmissionWithAssociatedProceedings'
					, exclusive: false
					, message: messages["sima.related.submission.notFound"]
					, test: function (proceedingIdentifier) {
						let isDRD = this.parent.typeOfSubmission === constants.typeOfSubmissions["Response to Disclosure-Related Deficiency Notice"];
						let isProceedingIdentifierFilled = !!proceedingIdentifier;
						let isRelatedSubmissionsFillable = (relatedSubmissionsRef.current.length > 1)
						//true means it passes validation, false means it fails
						return (isDRD && isProceedingIdentifierFilled && isRelatedSubmissionsFillable) || !isDRD
					}
				})
				, relatedSubmission: Yup.string().test({
					name: 'requiredOnlyIfDRDAndDropDownHasSelections'
					, exclusive: false
					, message: messages["common.isRequired"]
					, test: function (relatedSubmission) {
						let isDRD = this.parent.typeOfSubmission === constants.typeOfSubmissions["Response to Disclosure-Related Deficiency Notice"];
						let isProceedingIdentifierFilled = !!this.parent.proceedingIdentifier;
						let isRelatedSubmissionsFillable = (relatedSubmissionsRef.current.length > 1);
						let isRelatedSubmissionFilled = !!relatedSubmission;
						//true means it passes validation, false means it fails
						return (
							(isDRD && isProceedingIdentifierFilled && isRelatedSubmissionsFillable && isRelatedSubmissionFilled)
							|| !isDRD
							|| !isProceedingIdentifierFilled
							|| !isRelatedSubmissionsFillable
						);
					}
				})
			}
		}
	];
	let attachments = [
		{
			tabId: "attachments"
			, tabLabel: <FormattedMessage id="sima.tab.header.3" />
			, enableTabNumbering: true
			, tabContent:
				<>
				  <AttachmentsTabBody tempSubmissionId={tempSubmissionIdRef.current} />
				</>
			, validationSchema: {
				attachments: Yup.array().of(
					Yup.object()
				).test({
					name: 'minFilesPerSubmission'
					, exclusive: false
					, message: messages["error.noFiles"]
					, test: function (arr) {
						return arr.length > 0;
					}
				}).test({
					name: 'maxFilesPerSubmission'
					, exclusive: false
					, message: labels["error.tooManyFiles"]
					, test: function (arr) {
						return arr.length <= constants.maxUploadLimitPerSubmission;
					}
				}).test({
					name: 'fileCurrentlySubmitting'
					, exclusive: true
					, message: messages["error.uploadsIncomplete"]
					, test: function (arr) {
						let testResult = true;
						arr.forEach((attachment)=>{
							testResult &= ((attachment.uploadStatus + "") === "COMPLETE") || ((attachment.uploadStatus + "") === "FAILED")
						});
						return testResult;
					}
				})
			}
		}
	];
	let submit = [
		{
			tabId: "submit"
			, tabLabel: <FormattedMessage id="home.tab.submit" />
			, enableTabNumbering: true
			, tabContent:
				<SubmitTabBody codeTables={appContext.codeTables} navigation={navigation} displayReadOnlyWarning={true} checkSpamForEmailsWarning={true} displayCorrespondenceAgreementCertificationMsg={true} setErrorFocus={setErrorFocus} />
			, validationSchema: {
				certificationField: Yup.array().compact().length(1, messages["common.isRequired"])
			}
		}
	];


	const [intervalIds] = useState({});

	/**
	 * This function is meant to be called repeatedly through a setInterval()
	 * It either gradually shows or hides the tabs on the screen and then clears the interval when it has completed.
	 * If it's hiding the tabs, it will then automatically call the function to update the tabs.
	 *
	 * animationTimerID - either "show" or "hide", and accesses the object with that key for the intervalId to clear itself from continuous running
	 * startTime - the time when the function is first run, for figuring out what % to set the opacity to based on how long past the start time it is
	 * newArr - only used when we are subsequently updating the tabs array, just passed through to the function that updates the tabs
	 */
	const updateCSS = (animationTimerID, startTime, newArr, tabToClick) => {

		let duration = 200;

		let nextPercent = (Date.now() - startTime) / duration;
		if (nextPercent > 1) {
			nextPercent = 1;
		}

		if (document.getElementById("tabsDiv")) {
			document.getElementById("tabsDiv").style.opacity = (animationTimerID === "hide" ? 1 - nextPercent : nextPercent);
		}
		if (nextPercent === 1) {
			clearInterval(intervalIds[animationTimerID]);
			if (animationTimerID === "hide") {
				setTimeout(() => {updateTabsArr(newArr, tabToClick)});
			}
		}
	}

	/**
	 * This function will update the tabs, then call the function to show the tabs again.
	 *
	 * newArr - the array of tabs to set
	 */
	const updateTabsArr = (newArr, tabToClick) => {
		setData(newArr);

		if (!!tabToClick) {
			clickTab(tabToClick);
		}

		window.$(".wb-tabs-inited").removeClass("wb-tabs-inited");
		window.wb.add(".wb-tabs");

		setTimeout(setAndSaveIntervalToUpdateCSS, 500);       //--- this would be better to go off of the wb-ready.wb-tabs event, but this seems to cause react to skip the animation after the first 2 times
	}

	function initiateFadeOutFadeIn(newArr, tabToClick) {
		let startTime = Date.now();
		intervalIds["hide"] = setInterval(() => {
				updateCSS("hide", startTime, newArr, tabToClick);
			}
			, 1
		);
	}

	function tabArray(newArr) {
		if (!contains(newArr, eFilingMainInfo[0].tabId)) {
			newArr = newArr.concat(eFilingMainInfo);
		}
		if (!contains(newArr, details[0].tabId)) {
			newArr = newArr.concat(details);
		}
		if (!contains(newArr, attachments[0].tabId)) {
			newArr = newArr.concat(attachments);
		}
		if (!contains(newArr, submit[0].tabId)) {
			newArr = newArr.concat(submit);
		}

		return newArr;
	}

	function contains(arr, tabId) {
		let result = false;
		arr.map((item) => {
			if (item.tabId === tabId) {
				result = true;
			}
			return false;
		});
		return result;
	}


	function enforce2Digits(number) {
		if (number < 10) {
			number = '0'+number;
		}
		return number;
	}

	function getDate() {
		const today = new Date();
		const year = today.getFullYear();
		const month = enforce2Digits(today.getMonth() + 1);
		const date = enforce2Digits(today.getDate());
		return `${year}-${month}-${date}`;
	}

	function getTime() {
		const today = new Date();
		const hours = enforce2Digits(today.getHours());
		const minutes = enforce2Digits(today.getMinutes());
		const seconds = enforce2Digits(today.getSeconds());
		const TZ = today.getTimezoneOffset();
		let TZSigned;
		if (TZ > 0) {
			TZSigned = '-' + TZ/60 + ':' + enforce2Digits(TZ%60);
		} else {
			TZSigned = '+' + Math.abs(TZ/60) + ':' + enforce2Digits(TZ%60);
		}
		return `${hours}:${minutes}:${seconds} (UTC ${TZSigned})`;
	}


	async function navigateToSuccess(nowDate, nowTime, documentList, proceeding) {
		let apiURL = "contact";
		let apiParamsObj = {
			queryStringParameters:{
				"lang": currentLang()
			}
		}

		return API.get("backend-api", apiURL, apiParamsObj).then(function(response){
			setShowSpinner(false);

			let fName = response.firstname;
			let lName = response.lastname;
			let fullName = fName + ' ' + lName;

			navigation.navigate("SuccessPage", { nameOfSubmitter: fullName, submissionDate: nowDate, submissionTime: nowTime, documentList: documentList, proceedingName: proceeding });
		});
	}

	async function navigateToSemiConfirmation(nowDate, nowTime, documentList, proceeding) {
		let apiURL = "contact";
		let apiParamsObj = {
			queryStringParameters:{
				"lang": currentLang()
			}
		}

		return API.get("backend-api", apiURL, apiParamsObj).then(function(response){
			setShowSpinner(false);

			let fName = response.firstname;
			let lName = response.lastname;
			let fullName = fName + ' ' + lName;

			navigation.navigate("SemiConfirmation", { nameOfSubmitter: fullName, submissionDate: nowDate, submissionTime: nowTime, documentList: documentList, proceedingName: proceeding });
		});
	}

	function handleSubmit(values) {

		setShowSpinner(true);

		// TODO - fade back in if error caught or something
		initiateFadeOutFadeIn(data, 5);

		let clientAccountList = values.clientAccounts
		clientAccountList.forEach(a => {
			delete a.clientConfirm
			delete a.Name
		})

		let typeOfSubmissionOtherTransformed = [];
		if (!!values.typeOfSubmissionOther && values.typeOfSubmission.length > 0) {
			values.typeOfSubmissionOther.forEach((typeOfSubmission) => {
				typeOfSubmissionOtherTransformed.push({guid: typeOfSubmission});
			});
		}
		//compiling the JSON object to post to the API, including removing all the unnecessary items
		let apiPostObj = {
			  ace_proceeding             : values.proceedingIdentifier
			, ace_submittingpartycompany : accountData.ace_businessnumber	//required
			, ace_role                   : values.role
			, ace_firm                   : values.representativeCounsel
			, ace_subject                : values.subject
			, ace_TypeofSubmission       : values.typeOfSubmission	//required
			, ace_parentsubmission       : values.relatedSubmission
			, ace_othersubmissiontype    : typeOfSubmissionOtherTransformed
			, accounts                   : clientAccountList
		};

		if (apiPostObj.ace_subject === "") {
			delete apiPostObj.ace_subject;
		}
		if (apiPostObj.ace_proceeding === "") {
			delete apiPostObj.ace_proceeding;
		}

		let documentList = []; // for successful submission page

		//adding ace_documents if there were any attachments added
		if (!!values.attachments && values.attachments.length > 0) {

			let ace_documents = [];
			let ace_files_to_remove = [];
			for (const attachment of values.attachments) {

				if (attachment.uploadStatus === "COMPLETE") {
					ace_documents.push({ace_filename: attachment.fileName, ace_size: attachment.fileSize});
					documentList.push(attachment.fileName);
				} else {
					ace_files_to_remove.push(attachment);
				}
			}
			//making sure any failed file uploads are removed from the list -- somehow some are persisting
			if (ace_files_to_remove.length > 0) {
				values.attachments.reduce((acc, item) => {
					if (!ace_files_to_remove.includes(item)) {
						acc.push(item);
					}
					return acc;
				}, []);
			}

			apiPostObj.ace_documents = ace_documents;
			apiPostObj.tempSubmissionId = tempSubmissionIdRef.current;
		}

		let nowDate = getDate();
		let nowTime = getTime();
		let proceeding = values.proceedingLabelForReceipt;

		let apiURL = "uploadSubmission";
		let apiParamsObj = {
			queryStringParameters:{
				"lang": currentLang()
			}
			, body: apiPostObj
		}

		API.post("backend-api", apiURL, apiParamsObj).then(function(response){
			navigateToSuccess(nowDate, nowTime, documentList, proceeding);
		}).catch(function(error){
			if (window.env.environment !== "prod") {
				console.log({error})
			}
			setShowSpinner(false);
			setTimeout(appContext.setFlashError({duration:-1, messages:[labels["http.networkError"]]}), 500);	//making sure this error gets shown after navigate by making it async
			// TODO - Like fade back in here
			if (window.env.environment === "local") {
				navigation.navigate("SuccessPage", { nameOfSubmitter: "fullName", submissionDate: nowDate, submissionTime: nowTime, documentList: [], proceedingName: "proceeding" });
			}

			if (error.status === 504) {
				navigateToSemiConfirmation(nowDate, nowTime, documentList, proceeding);
			}
		});
	}

	const [data, setData] = useState([]);

	function allTabs() {
		let newArr = data;
		newArr = tabArray(newArr);
		return newArr;
	}


	useEffect(() => {
		setData(allTabs());
	}, []);


	const {scrollTo} = useScrollTo(config.molasses);	//<-- usage: https://github.com/TomasSestak/react-spring-scroll-to-hook
	return (
		<>
		  <Spinner show={showSpinner} />
		  <div className="row profile">
		    <div className="col-md-10">
		      <p><FormattedMessage id="home.description" /></p>
		    </div>
		  </div>
		  <div>
		    <Formik
		      initialValues={{
		      	certificationField: []
		      	, representative: ""
		      	, typeOfSubmission: ""
		      	, role: ""
		      	, administrativeInfo: ""
		      	, clientAccounts: []
		      	, typeOfSubmissionOther: []
		      	, confirmAddClient: ""
		      	, addClientRadio: ""
		      	, attachments: []
				, businessNumberForSubmission: accountBusinessNumber
		      }}
			  validateOnMount={true} // Triggers validation on mount
			  initialTouched={{clientAccounts: true}} // Using this to trigger validation of ClientAccounts Table immediately, instead of waiting for Submit to be clicked
			  validationSchema={Yup.object({
		      	...eFilingMainInfo[0].validationSchema
		      	, ...details[0].validationSchema
		      	, ...attachments[0].validationSchema
		      	, ...submit[0].validationSchema
		      })}
		      onSubmit={(values) => {
		      	handleSubmit(values);
		      }}
		    >
		      {({ errors, isValidating, isSubmitting, submitCount, setSubmitting, values }) => {
		      	if (submitCount > 0) {
		      		if (isSubmitting && isValidating && JSON.stringify(errors, null, 2) !== "{}") {
		      			let errorMsgs = [messages["comment.flash.validationErrors"]];
						setFirstError(Object.keys(errors)[0]);
		      			for (const [key, value] of Object.entries(errors)) {
							let errMsg = value;
							if (!!messages[key]) {
								errMsg = messages[key] + messages["label.colon"] + " " +errMsg;
							}
							let elementId;
							if (key === "certificationField") {
		      					elementId = document.getElementById(key+'-0');
							} else {
								elementId = document.getElementById(key);
							}
							let elementName = undefined;
							if (document.getElementsByName(key).length > 1) {
								if (key === "proceedingIdentifier") {
									elementName = document.getElementsByName(key)[1];
								}
							}
							console.log(elementName, elementId, key);
							errorMsgs.push(<ErrorValidationSpan errMsg={errMsg} elementName={elementName} elementId={elementId} keyEl={key} />);
		      			}
		      		  	appContext.setFlashError({duration:-1, messages: errorMsgs});
		      			if (!!document.getElementById("mouse-enter-e")) {
							scrollTo(document.getElementById("mouse-enter-e")); // possible fix to accelerate-> https://github.com/locomotivemtl/locomotive-scroll#instance-options
						} else {
							setTimeout(() => scrollTo(document.getElementById("mouse-enter-e")), 100);
						}
						setSubmitting(false); //prevent infinte refresh loops
		      		} else if (JSON.stringify(errors, null, 2) === "{}") {
		      			appContext.setFlashError(null)
		      		}
					// focus on the first error message in the error banner if there is any when pressing "Submit"
					if (errorFocus === true) {
						try {
							document.getElementById("span-"+firstError).focus({ preventScroll: true });
							setErrorFocus(false);
						} catch {}
					}
		      	}

		      	return (
		      		<Form>
		      			<WETTabs tabs={data} submitCount={submitCount} errors={errors} isSubmitting={isSubmitting} />
		      		</Form>
		      	)
		      }}
		    </Formik>
		    <AddClientFormik FieldArrayHelpersRef={FieldArrayHelpersRef} displayPopup={displayPopup} setDisplayPopup={setDisplayPopup} />
		  </div>
		</>
	);
}

SecureEFiling.path = "";
SecureEFiling.navigationOptions = {
	pageTitle: undefined
	, breadcrumb: [
		<PopupLink url={window.env["canada-url"][currentLang()]}><FormattedMessage id="breadcrumb.Canada.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.CBSA.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-sima-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.ACE.text" /></PopupLink>
	]
	, breadcrumbTitle: <FormattedMessage id="sima.btn.efiling"/>
};

export default SecureEFiling;